<template>
  <div class="table-content">
    <div class="table-wrap">
      <div class="table-title-div">
        <p class="table-title">{{ title }}</p>
        <div class="qaqc-scorecard-header-actions">
         <label v-if="expandableRowCount" class="qaqc-expand-all-wrap">
            <input
              ref="expandAllCheckboxInput"
              type="checkbox"
              class="qaqc-expand-all-checkbox"
              aria-label="Expand all rows with failed validations"
              @change="onExpandAllCheckboxChange"
            />
            <span class="qaqc-expand-all-text">Expand all</span>
          </label>
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="medium"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-table-content">
        <p class="qaqc-table-description">{{ description }}</p>
      <v-table class="test-data-table qaqc-table-wrap fd-table">
        <thead>
          <tr class="main-header">
            <th class="sticky-header-sub">Validation Check</th>
            <th class="sticky-header-sub text-middle">Pass Rate</th>
            <th class="sticky-header-sub text-middle">Passed</th>
            <th class="sticky-header-sub text-middle">Failed</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, idx) in rows" :key="idx">
            <tr>
              <td>
                <span class="qaqc-validation-matrix-check-with-note">
                  {{ row.check }}
                  <v-tooltip
                    v-if="row.checkDescription"
                    location="top"
                    content-class="qaqc-test-info-tooltip qaqc-validation-matrix-desc-tooltip"
                  >
                    <template #activator="{ props }">
                      <v-img
                        v-bind="props"
                        :src="infoIcon"
                        class="qaqc-test-info-icon qaqc-scorecard-header-note-icon"
                        width="18"
                        height="18"
                        alt=""
                        aria-label="Validation check description"
                      />
                    </template>
                    <div class="qaqc-test-tooltip-content qaqc-validation-matrix-desc-content">{{ row.checkDescription }}</div>
                  </v-tooltip>
                </span>
              </td>
              <td class="text-middle">{{ row.passRate }}</td>
              <td class="text-middle">{{ row.passed }}</td>
              <td
                class="text-middle qaqc-validation-matrix-failed-cell"
                :class="{ 'qaqc-validation-matrix-failed-cell--interactive': hasFailedCount(row) }"
                role="button"
                :tabindex="hasFailedCount(row) ? 0 : -1"
                @click="hasFailedCount(row) ? toggleFailedRow(idx) : null"
                @keydown.enter.prevent="hasFailedCount(row) ? toggleFailedRow(idx) : null"
                @keydown.space.prevent="hasFailedCount(row) ? toggleFailedRow(idx) : null"
              >
                <span class="qaqc-validation-matrix-failed-inner">
                  <v-icon
                    v-if="hasFailedCount(row)"
                    size="small"
                    class="qaqc-validation-matrix-failed-chevron"
                  >{{ expandedIndices.includes(idx) ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
                  <span class="qaqc-validation-matrix-failed-value">{{ row.failed }}</span>
                </span>
              </td>
            </tr>
            <tr v-if="expandedIndices.includes(idx) && hasFailedCount(row)" class="qaqc-validation-matrix-expand-row">
              <td colspan="4" class="qaqc-validation-matrix-expand-cell">
                <div class="qaqc-validation-matrix-expand-inner">
                  <span class="qaqc-consistency-sublabel">Failed samples (click to view QC details)</span>
                  <div v-if="failedSamplesList(row).length" class="qaqc-consistency-chips">
                    <span
                      v-for="(s, sidx) in failedSamplesList(row)"
                      :key="matrixChipDomKey(idx, s, sidx)"
                      class="qaqc-consistency-chip qaqc-validation-matrix-failed-chip"
                      :class="matrixChipClasses(idx, s, sidx)"
                      role="button"
                      :tabindex="matrixChipTabindex(idx, s, sidx)"
                      :aria-busy="matrixChipIsLoading(idx, s, sidx)"
                      @click.stop="openSampleQc(s, idx, sidx)"
                      @keydown.enter.prevent="activateFailedSampleChip(s, idx, sidx)"
                      @keydown.space.prevent="activateFailedSampleChip(s, idx, sidx)"
                    >
                      <v-progress-circular
                        v-if="matrixChipIsLoading(idx, s, sidx)"
                        indeterminate
                        size="18"
                        width="2"
                        color="primary"
                        class="qaqc-validation-matrix-chip-loader"
                      />
                      <template v-else>{{ s.fluidSampleID || s.id }}</template>
                    </span>
                  </div>
                  <div v-else class="qaqc-validation-matrix-no-ids">No sample IDs returned for this check.</div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { csvEscapeCell } from "./distributionCsvHelpers.js";
import {
  matrixContextToApiFilters,
  scorecardDatasetLabelToApiFilters,
  routerQueryFromApiFilters,
  pickSamplesListQueryForRouter,
} from "./qaqcSamplesListQuery.js";
import {
  resolveQaqcFailedSampleNavigation,
  pickTestFromSamplePayload,
  pushQaqcOverviewFailedSampleRoute,
} from "./qaqcFailedSampleNavigation.js";
import {
  mergeOverviewTabIntoQuery,
  overviewTabFromDatasetLabel,
  parseQaqcOverviewTabFromQuery,
} from "./qaqcOverviewTab.js";

export default {
  name: "ValidationPassRateMatrixTable",
  props: {
    rows: { type: Array, default: () => [] },
    title: {
      type: String,
      default: "Validation Pass Rate Matrix",
    },
    description: {
      type: String,
      default: "Percentage of all CCE samples passing each validation check",
    },
    matrixTestContext: {
      type: String,
      default: "cce",
      validator: (v) => v === "cce" || v === "dl" || v === "separator",
    },
    datasetLabel: { type: String, default: "" },
  },
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      infoIcon: require("@/assets/Svg/infoIcon.svg"),
      isCollapsed: false,
      expandedIndices: [],
      loadingMatrixChipKey: null,
    };
  },
  computed: {
    samplesListQueryForNavigation() {
      const label = String(this.datasetLabel || "").trim();
      const filters = label
        ? scorecardDatasetLabelToApiFilters(label)
        : matrixContextToApiFilters(this.matrixTestContext);
      return { type: "wells", ...routerQueryFromApiFilters(filters) };
    },
    expandableRowCount() {
      if (!this.rows?.length) return 0;
      let n = 0;
      this.rows.forEach((row) => {
        if (this.hasFailedCount(row)) n += 1;
      });
      return n;
    },
    allExpandableExpanded() {
      const idxs = [];
      this.rows.forEach((row, idx) => {
        if (this.hasFailedCount(row)) idxs.push(idx);
      });
      if (idxs.length === 0) return false;
      return idxs.every((i) => this.expandedIndices.includes(i));
    },
    expandAllIndeterminate() {
      const idxs = [];
      this.rows.forEach((row, idx) => {
        if (this.hasFailedCount(row)) idxs.push(idx);
      });
      if (idxs.length < 2) return false;
      let open = 0;
      idxs.forEach((i) => {
        if (this.expandedIndices.includes(i)) open += 1;
      });
      return open > 0 && open < idxs.length;
    },
  },
  watch: {
    rows() {
      this.expandedIndices = [];
      this.loadingMatrixChipKey = null;
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    expandableRowCount() {
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    expandedIndices: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncExpandAllCheckboxDom());
      },
    },
  },
  mounted() {
    this.$nextTick(() => this.syncExpandAllCheckboxDom());
  },
  methods: {
    failedCountNum(row) {
      const raw = row?.failed;
      if (raw === undefined || raw === null || raw === "-") return 0;
      const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? 0 : n;
    },
    hasFailedCount(row) {
      return this.failedCountNum(row) > 0;
    },
    failedSamplesList(row) {
      const list = row?.failedSamples;
      return Array.isArray(list) ? list : [];
    },
    toggleFailedRow(idx) {
      const pos = this.expandedIndices.indexOf(idx);
      if (pos === -1) this.expandedIndices.push(idx);
      else this.expandedIndices.splice(pos, 1);
    },
    syncExpandAllCheckboxDom() {
      const el = this.$refs.expandAllCheckboxInput;
      if (!el) return;
      el.indeterminate = this.expandAllIndeterminate;
      el.checked = this.allExpandableExpanded;
    },
    onExpandAllCheckboxChange(e) {
      const idxs = [];
      this.rows.forEach((row, idx) => {
        if (this.hasFailedCount(row)) idxs.push(idx);
      });
      if (idxs.length === 0) return;
      if (e.target.checked) {
        this.expandedIndices = [...idxs];
      } else {
        this.expandedIndices = [];
      }
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    resolveTenantId() {
      return getTenantPrefix() || "";
    },
    pushFailedSampleTestRoute(sampleId, routeTestType, testId, wellName, navigationTarget) {
      const baseQuery = {
        ...this.samplesListQueryForNavigation,
        ...pickSamplesListQueryForRouter(this.$route.query || {}),
      };
      const overviewTab =
        parseQaqcOverviewTabFromQuery(baseQuery) ||
        overviewTabFromDatasetLabel(this.datasetLabel);
      const listQuery = mergeOverviewTabIntoQuery(baseQuery, overviewTab);
      pushQaqcOverviewFailedSampleRoute(this.$router, {
        tenantId: this.resolveTenantId(),
        mongoSampleId: sampleId,
        testId,
        routeTestType,
        well: wellName,
        navigationTarget,
        listQuery,
      });
    },
    matrixChipInstanceKey(rowIdx, s, chipIdx) {
      return `r${rowIdx}:i${chipIdx}:${String(s.id ?? "")}:f${String(s.fluidSampleID ?? "")}`;
    },
    matrixChipDomKey(rowIdx, s, chipIdx) {
      return this.matrixChipInstanceKey(rowIdx, s, chipIdx);
    },
    matrixChipIsLoading(rowIdx, s, chipIdx) {
      return this.loadingMatrixChipKey === this.matrixChipInstanceKey(rowIdx, s, chipIdx);
    },
    matrixChipIsBusyOther(rowIdx, s, chipIdx) {
      return this.loadingMatrixChipKey != null && !this.matrixChipIsLoading(rowIdx, s, chipIdx);
    },
    matrixChipClasses(rowIdx, s, chipIdx) {
      return {
        "qaqc-validation-matrix-failed-chip--loading": this.matrixChipIsLoading(rowIdx, s, chipIdx),
        "qaqc-validation-matrix-failed-chip--disabled": this.matrixChipIsBusyOther(rowIdx, s, chipIdx),
      };
    },
    matrixChipTabindex(rowIdx, s, chipIdx) {
      if (this.matrixChipIsLoading(rowIdx, s, chipIdx)) return -1;
      if (this.matrixChipIsBusyOther(rowIdx, s, chipIdx)) return -1;
      return 0;
    },
    activateFailedSampleChip(sample, rowIdx, chipIdx) {
      if (this.loadingMatrixChipKey) return;
      this.openSampleQc(sample, rowIdx, chipIdx);
    },
    async fetchSamplePayloadForMatrixChip(sample) {
      if (sample?.id) {
        const res = await api.get(`public/api/samples/${sample.id}?viewSummary=true`);
        return this.samplePayloadFromViewSummary(res);
      }
      const fid = sample?.fluidSampleID;
      if (!fid) return null;
      const res = await api.get("public/api/samples", {
        params: {
          fluidSampleID: `["${String(fid)}"]`,
          viewSummary: true,
          pageNumber: 1,
          pageSize: 10,
        },
      });
      const samples = res?.data?.data?.samples;
      return Array.isArray(samples) && samples[0] ? samples[0] : null;
    },
    samplePayloadFromViewSummary(res) {
      const d = res?.data?.data;
      if (!d) return null;
      if (d.sample && typeof d.sample === "object") return d.sample;
      const list = d.samples;
      if (Array.isArray(list) && list[0]) return list[0];
      return null;
    },
    async openSampleQc(sample, rowIdx, chipIdx) {
      const instanceKey = this.matrixChipInstanceKey(rowIdx, sample, chipIdx);
      if (!sample || this.loadingMatrixChipKey) return;
      if (!sample.id && !sample.fluidSampleID) return;
      this.loadingMatrixChipKey = instanceKey;
      try {
        const s = await this.fetchSamplePayloadForMatrixChip(sample);
        if (!s) return;
        const mongoId = s.ID || s._id || s.id;
        if (!mongoId) return;

        const testKind =
          this.matrixTestContext === "separator"
            ? "separator"
            : this.matrixTestContext === "dl"
              ? "dl"
              : "cce";
        const { testId, routeTestType } = pickTestFromSamplePayload(s, testKind);
        const row = this.rows[rowIdx];
        const navigationTarget = resolveQaqcFailedSampleNavigation({
          criterionName: row?.check || "",
          source: testKind === "cce" ? "cceValidationMatrix" : "dlValidationMatrix",
          testKind,
        });

        if (testId && routeTestType) {
          const well = s.location?.well ?? s.wellName ?? s.well;
          this.pushFailedSampleTestRoute(mongoId, routeTestType, testId, well, navigationTarget);
        } else {
          const well = s.location?.well ?? s.wellName ?? s.well;
          if (well) {
            this.$router.push({
              name: "QAQCDetailView",
              params: { well: String(well) },
              query: this.samplesListQueryForNavigation,
            });
          } else {
            this.$router.push({
              name: "QAQCDetailView",
              query: this.samplesListQueryForNavigation,
            });
          }
        }
      } catch {
        void 0;
      } finally {
        this.loadingMatrixChipKey = null;
      }
    },
    downloadTableCSV() {
      if (!this.rows || this.rows.length === 0) {
        return;
      }
      const fileName = "validation_pass_rate_matrix.csv";
      const csvHeader = ["Validation Check", "Passed", "Failed", "Pass Rate", "Failed sample IDs"];
      const csvRows = this.rows.map((row) => {
        const ids = this.failedSamplesList(row)
          .map((s) => s.fluidSampleID || s.id)
          .filter(Boolean)
          .join(", ");
        return [
          csvEscapeCell(row.check ?? ""),
          csvEscapeCell(row.passed ?? ""),
          csvEscapeCell(row.failed ?? ""),
          csvEscapeCell(row.passRate ?? ""),
          csvEscapeCell(ids),
        ].join(",");
      });
      const csvContent =
        "\ufeff" + [csvHeader.map(csvEscapeCell).join(","), ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>
